import { useEffect, useState } from 'react';
import { X, Calendar, Clock, AlertTriangle, Gift } from 'lucide-react';
import type { SubscriptionPlanOption } from '../services/superAdminService';

export type GrantAccessMode = 'trial' | 'complimentary';

interface GrantBusinessAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (params: {
    planId: string;
    accessMode: GrantAccessMode;
    trialEndDate?: string;
    installAllApps: boolean;
    notes?: string;
  }) => Promise<void>;
  businessName: string;
  plans: SubscriptionPlanOption[];
  plansLoading: boolean;
  isLoading: boolean;
}

export default function GrantBusinessAccessModal({
  isOpen,
  onClose,
  onConfirm,
  businessName,
  plans,
  plansLoading,
  isLoading,
}: GrantBusinessAccessModalProps) {
  const [accessMode, setAccessMode] = useState<GrantAccessMode>('trial');
  const [planId, setPlanId] = useState('');
  const [trialEndDate, setTrialEndDate] = useState('');
  const [installAllApps, setInstallAllApps] = useState(true);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  const defaultEndDate = new Date();
  defaultEndDate.setDate(defaultEndDate.getDate() + 30);
  const defaultDate = defaultEndDate.toISOString().split('T')[0];

  useEffect(() => {
    if (!isOpen) return;
    if (!planId && plans.length > 0) {
      setPlanId(plans[0].plan_id);
    }
    if (!trialEndDate) {
      setTrialEndDate(defaultDate);
    }
  }, [isOpen, plans, planId, trialEndDate, defaultDate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!planId) {
      setError('Please select a plan');
      return;
    }

    if (accessMode === 'trial') {
      if (!trialEndDate) {
        setError('Please select a trial end date');
        return;
      }
      const endDate = new Date(trialEndDate);
      if (endDate <= new Date()) {
        setError('Trial end date must be in the future');
        return;
      }
    }

    try {
      await onConfirm({
        planId,
        accessMode,
        trialEndDate: accessMode === 'trial' ? trialEndDate : undefined,
        installAllApps,
        notes: notes.trim() || undefined,
      });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to grant access');
    }
  };

  const handleClose = () => {
    if (isLoading) return;
    onClose();
    setError('');
    setNotes('');
    setAccessMode('trial');
    setInstallAllApps(true);
  };

  if (!isOpen) return null;

  const selectedPlan = plans.find((p) => p.plan_id === planId);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-[60] p-4">
      <div
        className="bg-white rounded-t-[20px] sm:rounded-[20px] shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif' }}
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
              <Gift className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h3 className="text-[17px] font-semibold text-gray-900">Grant Access</h3>
              <p className="text-[13px] text-gray-500">{businessName}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="h-8 w-8 flex items-center justify-center rounded-full bg-gray-100 disabled:opacity-50"
          >
            <X className="h-4 w-4 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="rounded-[12px] p-3.5 bg-amber-50 border border-amber-200">
            <div className="flex items-start gap-2.5">
              <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-[13px] text-amber-800 leading-relaxed">
                Activates the account without Stripe billing — sets an active plan, installs apps,
                and lets the business log in immediately.
              </p>
            </div>
          </div>

          <div>
            <label className="block text-[13px] font-semibold text-gray-700 mb-2">Access type</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setAccessMode('trial')}
                disabled={isLoading}
                className={`px-3 py-2.5 rounded-[10px] text-[13px] font-semibold border transition-colors ${
                  accessMode === 'trial'
                    ? 'bg-blue-50 border-blue-200 text-blue-700'
                    : 'bg-gray-50 border-gray-200 text-gray-600'
                }`}
              >
                <Clock className="h-4 w-4 inline mr-1.5 -mt-0.5" />
                Trial
              </button>
              <button
                type="button"
                onClick={() => setAccessMode('complimentary')}
                disabled={isLoading}
                className={`px-3 py-2.5 rounded-[10px] text-[13px] font-semibold border transition-colors ${
                  accessMode === 'complimentary'
                    ? 'bg-green-50 border-green-200 text-green-700'
                    : 'bg-gray-50 border-gray-200 text-gray-600'
                }`}
              >
                <Gift className="h-4 w-4 inline mr-1.5 -mt-0.5" />
                No expiry
              </button>
            </div>
            <p className="mt-1.5 text-[12px] text-gray-500">
              {accessMode === 'trial'
                ? 'Access expires on the date you choose (auto-deactivated by cron).'
                : 'Stays active until you change it manually or they subscribe in Stripe.'}
            </p>
          </div>

          <div>
            <label htmlFor="grantPlanId" className="block text-[13px] font-semibold text-gray-700 mb-2">
              Plan
            </label>
            {plansLoading ? (
              <div className="text-[13px] text-gray-400 py-2">Loading plans...</div>
            ) : (
              <select
                id="grantPlanId"
                value={planId}
                onChange={(e) => setPlanId(e.target.value)}
                disabled={isLoading || plans.length === 0}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-[10px] text-[15px] focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
              >
                {plans.map((plan) => (
                  <option key={plan.plan_id} value={plan.plan_id}>
                    {plan.plan_name}
                    {plan.plan_price != null ? ` — $${plan.plan_price}/mo` : ''}
                    {plan.included_app_count > 0 ? ` (${plan.included_app_count} apps)` : ''}
                  </option>
                ))}
              </select>
            )}
            {selectedPlan && (
              <p className="mt-1 text-[12px] text-gray-500">
                {selectedPlan.included_app_count} apps included in this plan
              </p>
            )}
          </div>

          {accessMode === 'trial' && (
            <div>
              <label htmlFor="trialEndDate" className="block text-[13px] font-semibold text-gray-700 mb-2">
                <Calendar className="h-4 w-4 inline mr-1.5 -mt-0.5" />
                Trial end date
              </label>
              <input
                type="date"
                id="trialEndDate"
                value={trialEndDate}
                onChange={(e) => setTrialEndDate(e.target.value)}
                min={minDate}
                required
                disabled={isLoading}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-[10px] text-[15px] focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
              />
            </div>
          )}

          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={installAllApps}
              onChange={(e) => setInstallAllApps(e.target.checked)}
              disabled={isLoading}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
            />
            <span className="text-[13px] text-gray-700 leading-relaxed">
              Install all platform apps (not just plan-included). Extra apps are marked as admin-granted.
            </span>
          </label>

          <div>
            <label htmlFor="grantNotes" className="block text-[13px] font-semibold text-gray-700 mb-2">
              Notes (optional)
            </label>
            <textarea
              id="grantNotes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={isLoading}
              rows={2}
              placeholder="e.g. Extended demo for VivexPPF"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-[10px] text-[15px] focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 resize-none"
            />
          </div>

          {error && (
            <div className="rounded-[10px] p-3 bg-red-50 border border-red-200">
              <p className="text-[13px] text-red-800">{error}</p>
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              className="flex-1 px-4 py-3 text-[15px] font-semibold text-gray-700 bg-gray-100 rounded-[12px] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || plansLoading || plans.length === 0}
              className="flex-1 px-4 py-3 text-[15px] font-semibold bg-green-600 text-white rounded-[12px] disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Granting...
                </>
              ) : (
                'Grant Access'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
